import { Injectable } from '@angular/core';

export interface CourseSection {
  type: 'text' | 'list' | 'feature-grid' | 'timeline' | 'highlight';
  title?: string;
  content?: string;
  listItems?: string[];
  features?: { title: string; desc: string }[];
}

export interface CourseCta {
  title: string;
  subtitle: string;
  btnText: string;
}

export interface Course {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  shortDesc: string; 
  icon: string;
  color: string;
  image?: string;
  duration?: string;
  ages?: string;
  sections: CourseSection[];
  cta?: CourseCta;
  // keep for backward comp if other components rely on these
  fullDesc?: string;
  benefits?: string[];
  curriculum?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  private courses: Course[] = [
    {
      id: '1',
      slug: 'advanced-abacus',
      title: 'Advanced Abacus',
      subtitle: 'Mental Arithmetic & Brain Development Training',
      shortDesc: 'Enhance children\'s mental abilities through structured mathematical training.',
      icon: 'bi-calculator-fill',
      color: '#ff3366',
      duration: '1 Hour per Session',
      ages: '5 to 16 Years',
      sections: [
        {
          type: 'text',
          content: 'The Advanced Abacus Program is a powerful brain development course designed to enhance children\'s mental abilities through structured mathematical training. At Brainy International Institute of India, we use scientifically designed techniques to help children perform calculations with speed, accuracy, and confidence. This program enables students to visualize numbers mentally and solve complex arithmetic problems without using calculators, paper, or pen.'
        },
        {
          type: 'text',
          title: 'What is Abacus?',
          content: 'Abacus is an ancient mathematical tool used for performing calculations using beads. With regular practice, children learn to imagine the abacus in their mind and perform calculations mentally. This process activates both the left and right brain, improving overall cognitive development.'
        },
        {
          type: 'list',
          title: 'How Advanced Abacus Works',
          content: 'Students initially learn calculations using a physical abacus. Gradually, they transition to mental arithmetic, where they visualize the abacus and solve problems in seconds. This method helps children:',
          listItems: [
            'Think faster',
            'Improve memory retention',
            'Increase concentration',
            'Enhance problem-solving skills'
          ]
        },
        {
          type: 'list',
          title: 'Program Highlights',
          listItems: [
            'Beginner to Advanced Levels',
            'Structured and Progressive Curriculum',
            'Age Group: 5 to 16 Years',
            'Focus on Mental Arithmetic & Speed Calculation',
            'Individual Attention and Skill Assessment',
            'Regular Level-Based Examinations'
          ]
        },
        {
          type: 'list',
          title: 'What Students Will Learn',
          content: 'Through the Advanced Abacus Program, students will master:',
          listItems: [
            'Addition and Subtraction',
            'Multiplication and Division',
            'Percentage Calculations',
            'Square and Cube',
            'Square Root and Cube Root',
            'HCF and LCM',
            'Speed Calculation Techniques'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits of Advanced Abacus',
          features: [
            { title: 'Brain Development', desc: 'Enhances brain activity by engaging both hemispheres, leading to improved intelligence and faster thinking ability.' },
            { title: 'Improved Memory Power', desc: 'Develops strong memory retention and recall ability through visualization techniques.' },
            { title: 'High Concentration', desc: 'Improves focus and attention span, helping children perform better in academics.' },
            { title: 'Faster Calculation', desc: 'Enables children to solve complex problems within seconds.' },
            { title: 'Increased Confidence', desc: 'Boosts self-confidence as children gain mastery in mathematics and mental calculation.' },
            { title: 'Logical & Analytical Thinking', desc: 'Builds strong reasoning and problem-solving abilities.' }
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Our Abacus Program?',
          content: 'At Brainy International Institute of India, we focus on overall child development rather than just mathematics.',
          listItems: [
            '12+ Years of Experience',
            'Proven Teaching Methodology',
            'Child-Friendly Learning Approach',
            'School Integrated Programs',
            'Regular Competitions & Assessments',
            'Experienced Trainers'
          ]
        },
        {
          type: 'list',
          title: 'Teaching Method',
          listItems: [
            'Class Duration: Minimum 1 Hour per Session',
            'Level-Based Learning System',
            'Minimum 80% Required to Advance',
            'Free Revision Support for Students Who Need Improvement'
          ]
        },
        {
          type: 'text',
          title: 'Who Can Join?',
          content: 'Children aged 5 to 16 years can enroll in the Advanced Abacus Program. No prior knowledge is required. The program is designed for beginners as well as advanced learners.'
        }
      ],
      cta: {
        title: 'Unlock Your Child’s Hidden Potential',
        subtitle: 'Give your child the power of speed, accuracy, and confidence through our Advanced Abacus Program.',
        btnText: 'Enroll Now and Start the Journey Towards a Brighter Future'
      }
    },
    {
      id: '2',
      slug: 'diploma-montessori-elementary',
      title: 'Diploma in Montessori & Elementary Education',
      subtitle: 'Build a Rewarding Career in Teaching',
      shortDesc: 'Comprehensive training to teach preschool and elementary-level students effectively.',
      icon: 'bi-journal-richtext',
      color: '#4361ee',
      duration: 'Flexible (6-12 Months)',
      ages: 'Adults / Educators',
      sections: [
        {
          type: 'text',
          content: 'The Diploma in Montessori & Elementary Education is a comprehensive teacher training program designed to prepare individuals to teach both preschool and elementary-level students effectively. At Brainy International Institute of India, we combine Montessori teaching principles with modern elementary education methods to create confident, skilled, and professional educators.'
        },
        {
          type: 'list',
          title: 'About the Course',
          content: 'This program focuses on child-centered learning, helping teachers understand how children grow, learn, and respond at different stages. The course equips trainees with:',
          listItems: [
            'Strong teaching methodologies',
            'Classroom management skills',
            'Subject knowledge for elementary classes',
            'Effective communication skills',
            'Confidence to handle young learners'
          ]
        },
        {
          type: 'list',
          title: 'Course Highlights',
          listItems: [
            'Dual Training: Montessori + Elementary Education',
            'Practical and Activity-Based Learning',
            'Child Psychology & Development Training',
            'Lesson Planning & Teaching Strategies',
            'Classroom Management Techniques',
            'Teaching Aids & Learning Materials Training',
            'Practice Teaching Sessions',
            'Certification upon completion'
          ]
        },
        {
          type: 'timeline',
          title: 'Course Modules',
          features: [
            { title: '1. Montessori Education Fundamentals', desc: 'Principles of teaching, Role of an educator, Child-centered learning approach, Use of materials.' },
            { title: '2. Child Psychology & Development', desc: 'Growth stages, Understanding behavior, Learning styles, Emotional and social development.' },
            { title: '3. Elementary Education Methodology', desc: 'Teaching methods for elementary classes, Subject-wise strategies, Interactive learning.' },
            { title: '4. Language Development', desc: 'Phonics, Grammar basics, Vocabulary, Communication skills.' },
            { title: '5. Mathematics Teaching', desc: 'Basic arithmetic, Activity-based math teaching, Problem-solving techniques.' },
            { title: '6. Environmental Studies (EVS)', desc: 'Teaching environmental concepts, Activity-based learning, Awareness development.' },
            { title: '7. Classroom Management', desc: 'Handling young learners effectively, Discipline strategies, Creating a positive environment.' },
            { title: '8. Teaching Aids & Practical Training', desc: 'Preparation of teaching materials, Demonstration, Practice sessions.' },
            { title: '9. Assessment & Evaluation', desc: 'Student performance assessment, Conducting tests, Feedback methods.' }
          ]
        },
        {
          type: 'list',
          title: 'Who Can Join?',
          content: 'No prior teaching experience is required.',
          listItems: [
            'Aspiring teachers',
            'Graduates and diploma holders',
            'Homemakers looking for career opportunities',
            'Individuals passionate about teaching children'
          ]
        },
        {
          type: 'list',
          title: 'Career Opportunities',
          content: 'After completing this diploma, candidates can work as:',
          listItems: [
            'Montessori Teacher',
            'Elementary School Teacher',
            'Kindergarten Teacher',
            'Preschool Coordinator',
            'Private Tutor',
            'Start their own preschool or tuition center'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits',
          features: [
            { title: 'Dual Teaching Skills', desc: 'Gain expertise in both Montessori and elementary education methods.' },
            { title: 'Practical Training', desc: 'Learn real classroom techniques through hands-on experience.' },
            { title: 'Career Growth', desc: 'Wide range of job opportunities in schools and education institutes.' },
            { title: 'Confidence Building', desc: 'Develop strong teaching confidence and classroom handling skills.' },
            { title: 'Communication Skills', desc: 'Improve interaction with students and parents.' }
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Us?',
          content: 'At Brainy International Institute of India, we focus on quality training and student success.',
          listItems: [
            'Experienced trainers',
            'Structured and practical curriculum',
            'Activity-based teaching approach',
            'Supportive learning environment',
            'Certification and guidance',
            'Placement assistance'
          ]
        },
        {
          type: 'list',
          title: 'Teaching Method',
          listItems: [
            'Interactive classroom sessions',
            'Demonstration-based learning',
            'Activity and practice-oriented training',
            'Continuous assessment and feedback',
            'Real-time teaching practice'
          ]
        },
        {
          type: 'text',
          title: 'Certification',
          content: 'Students will receive a Diploma in Montessori & Elementary Education upon successful completion of the course.'
        }
      ],
      cta: {
        title: 'Start Your Teaching Career Today',
        subtitle: 'Turn your passion for teaching into a successful profession with our Diploma in Montessori & Elementary Education.',
        btnText: 'Enroll Now and Shape Young Minds'
      }
    },
    {
      id: '3',
      slug: 'diploma-montessori-primary',
      title: 'Diploma in Montessori & Primary Education',
      subtitle: 'Build a Strong Career in Teaching',
      shortDesc: 'Blend of Montessori methods and modern primary education techniques.',
      icon: 'bi-easel2-fill',
      color: '#f72585',
      duration: 'Flexible (6-12 Months)',
      ages: 'Adults / Educators',
      sections: [
        {
          type: 'text',
          content: 'The Diploma in Montessori & Primary Education is a comprehensive teacher training program designed to prepare individuals for teaching both preschool and primary-level students. At Brainy International Institute of India, we provide a perfect blend of Montessori teaching methods and modern primary education techniques, ensuring that trainees are fully equipped to handle children across different age groups.'
        },
        {
          type: 'list',
          title: 'About the Course',
          content: 'This diploma program focuses on child-centered education, combining Montessori principles with structured primary school teaching methodologies. The course is designed to develop:',
          listItems: [
            'Teaching skills',
            'Classroom management abilities',
            'Subject knowledge',
            'Communication skills',
            'Confidence in handling young learners'
          ]
        },
        {
          type: 'list',
          title: 'Course Highlights',
          listItems: [
            'Dual Training: Montessori + Primary Education',
            'Practical and Activity-Based Learning',
            'Child Psychology & Development',
            'Lesson Planning & Teaching Techniques',
            'Classroom Management Skills',
            'Teaching Aids & Learning Materials Training',
            'Internship / Practical Exposure',
            'Certification upon completion'
          ]
        },
        {
          type: 'timeline',
          title: 'Course Modules',
          features: [
            { title: '1. Montessori Education Fundamentals', desc: 'Principles of teaching, Role of the teacher, Child-centered approach, Use of materials.' },
            { title: '2. Child Psychology & Development', desc: 'Stages of child growth, Understanding behavior, Emotional and social development.' },
            { title: '3. Primary Education Methodology', desc: 'Teaching techniques for primary classes, Subject-wise strategies, Interactive learning.' },
            { title: '4. Language Development', desc: 'Phonics and reading techniques, Grammar basics, Vocabulary, Communication.' },
            { title: '5. Mathematics Teaching', desc: 'Basic arithmetic concepts, Activity-based math teaching, Simplified teaching techniques.' },
            { title: '6. Environmental Science & GK', desc: 'Teaching EVS concepts, Activity-based learning, Developing curiosity and awareness.' },
            { title: '7. Classroom Management', desc: 'Handling children effectively, Discipline techniques, Creating a positive environment.' },
            { title: '8. Teaching Aids & Practical Training', desc: 'Preparation of teaching aids, Demonstration classes, Practice sessions.' },
            { title: '9. Assessment & Evaluation', desc: 'Student evaluation, Conducting tests, Feedback and improvement techniques.' }
          ]
        },
        {
          type: 'list',
          title: 'Who Can Join?',
          content: 'No prior teaching experience is required.',
          listItems: [
            'Aspiring teachers',
            'Graduates and diploma holders',
            'Homemakers looking to restart careers',
            'Individuals passionate about teaching'
          ]
        },
        {
          type: 'list',
          title: 'Career Opportunities',
          content: 'After completing the diploma, candidates can work as:',
          listItems: [
            'Montessori Teacher',
            'Primary School Teacher',
            'Kindergarten Teacher',
            'Preschool Coordinator',
            'Private Tutor',
            'Start their own preschool or coaching center'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits',
          features: [
            { title: 'Dual Qualification Advantage', desc: 'Get trained in both Montessori and Primary Education methods.' },
            { title: 'Practical Teaching Skills', desc: 'Gain real classroom experience through hands-on training.' },
            { title: 'Career Opportunities', desc: 'Wide range of job opportunities in schools and education centers.' },
            { title: 'Confidence & Communication', desc: 'Develop strong teaching confidence and communication skills.' },
            { title: 'Flexible Learning', desc: 'Suitable for beginners and career restart candidates.' }
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Us?',
          content: 'At Brainy International Institute of India, we provide quality training with a focus on practical teaching.',
          listItems: [
            'Experienced and qualified trainers',
            'Structured curriculum',
            'Practical training sessions',
            'Student-friendly learning environment',
            'Certification support',
            'Placement guidance'
          ]
        },
        {
          type: 'list',
          title: 'Teaching Method',
          listItems: [
            'Interactive classroom sessions',
            'Demonstration-based teaching',
            'Activity-based learning',
            'Practice teaching sessions',
            'Continuous assessment and feedback'
          ]
        },
        {
          type: 'text',
          title: 'Certification',
          content: 'Students will receive a Diploma in Montessori & Primary Education upon successful completion of the course.'
        }
      ],
      cta: {
        title: 'Start Your Journey as a Professional Teacher',
        subtitle: 'Build a rewarding career in education with our Diploma in Montessori & Primary Education.',
        btnText: 'Enroll Now and Shape the Future'
      }
    },
    {
      id: '4',
      slug: 'handwriting-improvement',
      title: 'Handwriting Improvement',
      subtitle: 'Write Neatly. Write Confidently.',
      shortDesc: 'Develop clear, neat, and well-structured handwriting with confidence.',
      icon: 'bi-pen-fill',
      color: '#7209b7',
      duration: 'Flexible',
      ages: 'School students (all grades)',
      sections: [
        {
          type: 'text',
          content: 'The Handwriting Improvement Program is designed to help students develop clear, neat, and well-structured handwriting. At Brainy International Institute of India, we focus on improving writing skills through systematic practice, proper techniques, and guided training.'
        },
        {
          type: 'list',
          title: 'Why Handwriting Matters',
          content: 'Good handwriting plays an important role in a child’s academic success. Neat and legible writing helps:',
          listItems: [
            'Improve presentation in exams',
            'Increase confidence in writing',
            'Create a positive impression on teachers',
            'Enhance clarity of thoughts'
          ]
        },
        {
          type: 'list',
          title: 'Program Highlights',
          listItems: [
            'Step-by-step handwriting improvement techniques',
            'Focus on letter formation and spacing',
            'Speed and neatness training',
            'Individual attention for each student',
            'Practice worksheets and guided exercises'
          ]
        },
        {
          type: 'list',
          title: 'What Students Will Learn',
          content: 'Students will be trained in:',
          listItems: [
            'Proper letter formation (A–Z / a–z)',
            'Correct spacing between letters and words',
            'Sentence writing practice',
            'Writing alignment and structure',
            'Speed writing without losing clarity',
            'Cursive writing (optional)'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits of Handwriting Training',
          features: [
            { title: 'Improved Writing Clarity', desc: 'Students learn to write neatly and legibly.' },
            { title: 'Better Presentation', desc: 'Improves overall answer sheet presentation in exams.' },
            { title: 'Increased Writing Speed', desc: 'Helps students complete exams on time.' },
            { title: 'Boosted Confidence', desc: 'Students feel more confident in their written work.' },
            { title: 'Strong Motor Skills', desc: 'Improves hand coordination and control.' }
          ]
        },
        {
          type: 'list',
          title: 'Our Teaching Method',
          content: 'At Brainy International Institute of India, we follow a structured and student-friendly approach:',
          listItems: [
            'Guided writing practice',
            'Daily improvement tracking',
            'Personalized correction and feedback',
            'Practice worksheets for consistency',
            'Fun and engaging writing activities'
          ]
        },
        {
          type: 'list',
          title: 'Who Can Join?',
          listItems: [
            'School students (all grades)',
            'Children with poor or unclear handwriting',
            'Students who want to improve writing speed and neatness'
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Us?',
          listItems: [
            'Experienced trainers',
            'Proven improvement techniques',
            'Individual attention',
            'Structured learning system',
            'Visible results in a short time'
          ]
        }
      ],
      cta: {
        title: 'Improve Handwriting. Improve Confidence.',
        subtitle: 'Help your child develop neat, clear, and confident handwriting.',
        btnText: 'Enroll Today and See the Difference'
      }
    },
    {
      id: '5',
      slug: 'montessori-teacher-training',
      title: 'Montessori Teacher Training (DMTT)',
      subtitle: 'Diploma in Montessori Teacher Training',
      shortDesc: 'Build a successful career in early childhood education.',
      icon: 'bi-person-hearts',
      color: '#3a0ca3',
      duration: '3 to 12 Months',
      ages: 'Adults / Educators',
      sections: [
        {
          type: 'text',
          content: 'The Diploma in Montessori Teacher Training (DMTT) is a comprehensive program designed for individuals who aspire to build a successful career in early childhood education. At Brainy International Institute of India, we provide practical, hands-on training based on Montessori principles to prepare confident and skilled educators for preschool and primary teaching.'
        },
        {
          type: 'text',
          title: 'About the Course',
          content: 'The Montessori method focuses on child-centered learning, where children learn through exploration, activity, and self-directed experiences. Our DMTT program equips trainees with the knowledge, teaching skills, and classroom management techniques required to handle young learners effectively.'
        },
        {
          type: 'list',
          title: 'Course Highlights',
          listItems: [
            'Diploma Certification in Montessori Teacher Training',
            'Practical and Activity-Based Learning',
            'Child Psychology & Development Training',
            'Classroom Management Techniques',
            'Teaching Aids & Montessori Materials Training',
            'Internship / Practical Exposure',
            'Placement Guidance Support'
          ]
        },
        {
          type: 'timeline',
          title: 'Course Modules',
          features: [
            { title: '1. Introduction to Montessori Method', desc: 'Principles of education, Role of a teacher, Understanding child-centered learning.' },
            { title: '2. Child Psychology & Development', desc: 'Stages of child development, Behavior management, Understanding learning patterns.' },
            { title: '3. Teaching Methodology', desc: 'Lesson planning techniques, Classroom interaction methods, Activity-based approach.' },
            { title: '4. Montessori Materials Training', desc: 'Use of Montessori equipment, Practical demonstrations, Hands-on training sessions.' },
            { title: '5. Language Development', desc: 'Phonics and reading techniques, Vocabulary building, Communication skill development.' },
            { title: '6. Mathematics Teaching', desc: 'Basic number concepts, Activity-based math teaching, Teaching aids for better understanding.' },
            { title: '7. Classroom Management', desc: 'Handling preschool children, Discipline strategies, Creating a positive learning environment.' },
            { title: '8. Practical Training & Assessment', desc: 'Teaching practice sessions, Assignments and evaluations, Final assessment.' }
          ]
        },
        {
          type: 'list',
          title: 'Who Can Join?',
          content: 'No prior teaching experience is required.',
          listItems: [
            'Aspiring teachers',
            'Homemakers looking to restart careers',
            'Graduates interested in teaching',
            'Individuals passionate about working with children'
          ]
        },
        {
          type: 'list',
          title: 'Career Opportunities',
          content: 'After completing the DMTT program, candidates can work as:',
          listItems: [
            'Montessori Teacher',
            'Preschool Teacher',
            'Kindergarten Teacher',
            'Daycare Educator',
            'Private Tutor',
            'Start their own preschool or daycare center'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits of DMTT',
          features: [
            { title: 'Professional Certification', desc: 'Gain a recognized diploma to start your teaching career.' },
            { title: 'Practical Teaching Skills', desc: 'Learn real classroom techniques through hands-on training.' },
            { title: 'Career Growth', desc: 'Opens opportunities in schools, preschools, and education centers.' },
            { title: 'Confidence Building', desc: 'Develop confidence in handling children and teaching effectively.' },
            { title: 'Flexible Learning', desc: 'Suitable for beginners and career restart candidates.' }
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Us?',
          content: 'At Brainy International Institute of India, we ensure quality training and career support.',
          listItems: [
            'Experienced Trainers',
            'Practical Training Approach',
            'Student-Friendly Environment',
            'Certification Support',
            'Placement Assistance',
            'Proven Training Methodology'
          ]
        },
        {
          type: 'list',
          title: 'Teaching Method',
          listItems: [
            'Interactive classroom sessions',
            'Demonstration-based teaching',
            'Hands-on practical training',
            'Assignments and assessments',
            'Continuous guidance and feedback'
          ]
        },
        {
          type: 'text',
          title: 'Certification',
          content: 'Students will receive a Diploma in Montessori Teacher Training (DMTT) upon successful completion of the course.'
        }
      ],
      cta: {
        title: 'Start Your Teaching Career Today',
        subtitle: 'Turn your passion for teaching into a rewarding profession with our Montessori Teacher Training Program.',
        btnText: 'Enroll Now and Build a Successful Future'
      }
    },
    {
      id: '6',
      slug: 'spoken-english',
      title: 'Spoken English',
      subtitle: 'Build Confidence. Speak Fluently.',
      shortDesc: 'Build Confidence. Speak Fluently in real-life situations.',
      icon: 'bi-chat-quote-fill',
      color: '#f8961e',
      duration: 'Flexible',
      ages: '6+ Years & Adults',
      sections: [
        {
          type: 'text',
          content: 'The Spoken English Program is designed to help students communicate confidently and effectively in English. At Brainy International Institute of India, we focus on practical communication skills that enable students to speak English fluently in real-life situations.'
        },
        {
          type: 'list',
          title: 'Why Spoken English is Important',
          content: 'In today’s world, English communication plays a key role in academic success, career opportunities, social confidence, and personality development. Strong communication skills help students express their thoughts clearly and confidently.',
          listItems: [
            'Academic success',
            'Career opportunities',
            'Social confidence',
            'Personality development'
          ]
        },
        {
          type: 'list',
          title: 'Program Highlights',
          listItems: [
            'Beginner to Advanced Levels',
            'Practical Speaking Sessions',
            'Interactive Learning Environment',
            'Vocabulary Building Activities',
            'Real-Life Conversation Practice',
            'Personality Development Focus'
          ]
        },
        {
          type: 'list',
          title: 'What Students Will Learn',
          content: 'Our Spoken English program covers:',
          listItems: [
            'Basic to Advanced Grammar',
            'Sentence Formation',
            'Daily Conversation Skills',
            'Vocabulary Improvement',
            'Pronunciation and Accent Training',
            'Public Speaking Skills'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits of Spoken English Training',
          features: [
            { title: 'Improved Communication Skills', desc: 'Students learn to express their ideas clearly and confidently.' },
            { title: 'Increased Confidence', desc: 'Overcomes hesitation and fear of speaking in English.' },
            { title: 'Better Pronunciation', desc: 'Helps students speak clearly with proper pronunciation.' },
            { title: 'Enhanced Vocabulary', desc: 'Builds strong word power for effective communication.' },
            { title: 'Personality Development', desc: 'Improves overall personality and presentation skills.' }
          ]
        },
        {
          type: 'list',
          title: 'Our Teaching Method',
          content: 'At Brainy International Institute of India, we follow a practical and student-friendly approach:',
          listItems: [
            'Activity-based learning',
            'Role plays and group discussions',
            'Daily speaking practice',
            'Real-life conversation training',
            'Continuous feedback and improvement'
          ]
        },
        {
          type: 'list',
          title: 'Who Can Join?',
          listItems: [
            'School Students',
            'Beginners with no English background',
            'Students who want to improve fluency',
            'Anyone looking to build confidence in speaking'
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Us?',
          listItems: [
            'Experienced Trainers',
            'Friendly Learning Environment',
            'Practical Communication Focus',
            'Individual Attention',
            'Proven Results'
          ]
        }
      ],
      cta: {
        title: 'Speak with Confidence. Succeed Everywhere.',
        subtitle: 'Give your child the confidence to communicate in English fluently and effectively.',
        btnText: 'Enroll Today and Start Speaking'
      }
    },
    {
      id: '7',
      slug: 'vedic-maths',
      title: 'Vedic Maths',
      subtitle: 'Speed Math & Smart Calculation Training',
      shortDesc: 'Speed Math & Smart Calculation Training for faster problem solving.',
      icon: 'bi-lightning-charge-fill',
      color: '#4cc9f0',
      duration: 'Level-Based',
      ages: '8+ Years',
      sections: [
        {
          type: 'text',
          content: 'The Vedic Maths Program is a powerful and easy-to-learn system that helps students solve mathematical problems faster and more efficiently using simple techniques and shortcuts. At Brainy International Institute of India, we train students to master mathematics using logical methods that reduce calculation time and improve accuracy.'
        },
        {
          type: 'text',
          title: 'What is Vedic Maths?',
          content: 'Vedic Maths is an ancient system of mathematics based on simple formulas (sutras) that make calculations quicker and easier. These techniques help students solve complex problems mentally without relying on calculators or lengthy traditional methods.'
        },
        {
          type: 'list',
          title: 'How Vedic Maths Helps Students',
          content: 'Vedic Maths transforms the way children approach mathematics by making it simple to understand, quick to apply, fun to learn, and easy to remember. Students can solve problems in seconds, improving both speed and confidence.',
          listItems: [
            'Simple to understand',
            'Quick to apply',
            'Fun to learn',
            'Easy to remember'
          ]
        },
        {
          type: 'list',
          title: 'Program Highlights',
          listItems: [
            'Easy-to-learn shortcut techniques',
            'Step-by-step structured training',
            'Suitable for school students',
            'Focus on speed and accuracy',
            'Practical problem-solving approach',
            'Regular practice and assessments'
          ]
        },
        {
          type: 'list',
          title: 'What Students Will Learn',
          content: 'Students will learn various Vedic Maths techniques to solve:',
          listItems: [
            'Addition and Subtraction (fast methods)',
            'Multiplication tricks',
            'Division shortcuts',
            'Squares and Square Roots',
            'Cubes and Cube Roots',
            'Algebra basics',
            'Speed calculation techniques'
          ]
        },
        {
          type: 'feature-grid',
          title: 'Key Benefits of Vedic Maths',
          features: [
            { title: 'Faster Calculations', desc: 'Solve mathematical problems in seconds using shortcut methods.' },
            { title: 'Improved Accuracy', desc: 'Reduces mistakes by simplifying complex calculations.' },
            { title: 'Strong Number Sense', desc: 'Builds a deeper understanding of numbers and patterns.' },
            { title: 'Boosts Confidence', desc: 'Students feel more confident while solving math problems in exams.' },
            { title: 'Saves Time in Exams', desc: 'Helps students complete exams faster with better accuracy.' },
            { title: 'Enhances Logical Thinking', desc: 'Improves analytical and reasoning skills.' }
          ]
        },
        {
          type: 'list',
          title: 'Why Choose Our Vedic Maths Program?',
          content: 'At Brainy International Institute of India, we focus on making mathematics easy and enjoyable.',
          listItems: [
            'Experienced Trainers',
            'Proven Teaching Techniques',
            'Student-Friendly Approach',
            'Practical Learning Methods',
            'Individual Attention',
            'Regular Practice Sessions'
          ]
        },
        {
          type: 'list',
          title: 'Who Can Join?',
          content: 'No advanced math knowledge is required to start.',
          listItems: [
            'School Students (Age 8+)',
            'Students preparing for competitive exams',
            'Children who want to improve speed in mathematics'
          ]
        },
        {
          type: 'list',
          title: 'Teaching Method',
          listItems: [
            'Interactive and activity-based sessions',
            'Shortcut techniques with real-time examples',
            'Practice worksheets and exercises',
            'Regular performance evaluation'
          ]
        }
      ],
      cta: {
        title: 'Make Math Easy, Fast & Fun',
        subtitle: 'Help your child overcome fear of mathematics and become confident in calculations.',
        btnText: 'Enroll in Vedic Maths Today'
      }
    }
  ];

  constructor() { }

  getAllCourses(): Course[] {
    return this.courses;
  }

  getCourseBySlug(slug: string): Course | undefined {
    return this.courses.find(c => c.slug === slug);
  }
}
